import React, { useState } from 'react';
import { DeliveryMethod, User } from '../../types';
import { LABELS } from '../../constants/labels';
import { ArrowRight, User as UserIcon, Phone, Mail, MapPin, Hash, ShieldCheck, Lock } from 'lucide-react';
import { FormContainer, Input, Button } from '../ui/DesignSystem';

export interface CheckoutFields {
    name: string;
    phone: string;
    email: string;
    /** Gatuadress. Bara vid hemleverans. */
    address: string;
    postalCode: string;
    city: string;
}

interface CheckoutFormProps {
    student?: User;
    studentCode: string;
    setStudentCode: (val: string) => void;
    form: CheckoutFields;
    setForm: (val: CheckoutFields) => void;
    /** Styr adressfälten och knappen ("Fortsätt till betalning" / "Beställ och betala Anna"). */
    deliveryMethod: DeliveryMethod;
    /** Kunden betalar i kassan härnäst (Stripe), vid hemleverans eller hämtning. */
    paysInCheckout?: boolean;
    /**
     * Kunden har tackat nej till erbjudandemejlet efter kampanjen. Servern
     * sparar beskedet på e-postadressen, så att det gäller även nästa köp.
     */
    offerDeclined: boolean;
    setOfferDeclined: (val: boolean) => void;
    /** Säljarens förnamn, till knappen när kunden betalar säljaren. */
    sellerFirstName?: string;
    onSubmit: (e: React.FormEvent) => void;
    isSubmitting: boolean;
    disabled?: boolean;
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
    student, studentCode, setStudentCode, form, setForm, deliveryMethod, paysInCheckout,
    offerDeclined, setOfferDeclined, sellerFirstName = 'säljaren', onSubmit, isSubmitting, disabled
}) => {
    const [termsAccepted, setTermsAccepted] = useState(false);
    const isHome = deliveryMethod === 'HOME';
    const set = (patch: Partial<CheckoutFields>) => setForm({ ...form, ...patch });

    return (
        <form onSubmit={onSubmit}>
            <FormContainer className="space-y-4">
                {!student && (
                    <div className="border-b border-stone-100 pb-4 mb-2">
                        <Input
                            required
                            aria-label="Säljkod"
                            autoComplete="off"
                            placeholder={`Ange säljkod (t.ex. anna-123)`}
                            value={studentCode}
                            onChange={e => setStudentCode(e.target.value)}
                            icon={<Hash size={16} />}
                        />
                        <p className="text-[10px] text-stone-400 mt-2 px-1">Ange koden för den {LABELS.role.student.toLowerCase()} du vill handla av.</p>
                    </div>
                )}

                <div>
                    <h4 className="text-xs font-black uppercase tracking-widest text-stone-400 mb-4 px-1">
                        Dina Uppgifter
                    </h4>

                    <div className="space-y-3">
                        <Input
                            id="name"
                            name="name"
                            required
                            aria-label="För- och efternamn"
                            autoComplete="name"
                            maxLength={120}
                            placeholder="För- och efternamn"
                            value={form.name}
                            onChange={e => set({ name: e.target.value })}
                            icon={<UserIcon size={16} />}
                        />

                        <Input
                            id="email"
                            name="email"
                            required
                            type="email"
                            aria-label="E-postadress"
                            autoComplete="email"
                            maxLength={254}
                            placeholder="E-postadress"
                            value={form.email}
                            onChange={e => set({ email: e.target.value })}
                            icon={<Mail size={16} />}
                        />
                        {/* Erbjudandemejlet efter kampanjen: kunden informeras här och kan tacka nej direkt (soft opt-in). */}
                        <p className="text-[11px] text-stone-500 leading-snug px-1 -mt-1" aria-live="polite">
                            {offerDeclined ? (
                                <>
                                    Klart, du får inget erbjudande från oss. Vi mejlar dig bara om din order.{' '}
                                    <button type="button" onClick={() => setOfferDeclined(false)} className="underline text-stone-700 hover:text-coffee-600">Ångra</button>
                                </>
                            ) : (
                                <>
                                    Vi mejlar dig om din order. Efter kampanjen skickar vi ett enda mejl med ett erbjudande, inget spam. Vill du inte ha det?{' '}
                                    <button type="button" onClick={() => setOfferDeclined(true)} className="underline font-bold text-stone-700 hover:text-coffee-600">Klicka här.</button>
                                </>
                            )}
                        </p>

                        {/* Telefonen krävs vid betalning i kassan (Stripe och fraktbolagets avisering). */}
                        <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            required={paysInCheckout}
                            aria-label={paysInCheckout ? 'Telefonnummer' : 'Telefonnummer (valfritt)'}
                            aria-describedby={paysInCheckout ? 'phone-hint' : undefined}
                            autoComplete="tel"
                            inputMode="tel"
                            minLength={7}
                            maxLength={20}
                            placeholder={paysInCheckout ? 'Telefonnummer' : 'Telefonnummer (valfritt)'}
                            value={form.phone}
                            onChange={e => set({ phone: e.target.value })}
                            icon={<Phone size={16} />}
                        />
                        {paysInCheckout && (
                            <p id="phone-hint" className="text-[11px] text-stone-500 px-1 leading-relaxed">
                                Behövs för betalningen{isHome ? ' och för att fraktbolaget ska kunna avisera paketet' : ''}.
                            </p>
                        )}
                    </div>
                </div>

                {/* Leveransadress, bara vid hemleverans. Vid hämtning behövs ingen adress. */}
                {isHome && (
                    <div>
                        <h4 className="text-xs font-black uppercase tracking-widest text-stone-400 mb-4 px-1">
                            Leveransadress
                        </h4>
                        <div className="space-y-3">
                            <Input
                                id="address"
                                name="address"
                                required
                                aria-label="Gatuadress"
                                autoComplete="shipping street-address"
                                minLength={3}
                                maxLength={200}
                                placeholder="Gatuadress"
                                value={form.address}
                                onChange={e => set({ address: e.target.value })}
                                icon={<MapPin size={16} />}
                            />
                            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
                                <Input
                                    id="postal-code"
                                    name="postal-code"
                                    required
                                    aria-label="Postnummer"
                                    autoComplete="shipping postal-code"
                                    inputMode="numeric"
                                    pattern="[1-9][0-9]{2} ?[0-9]{2}"
                                    title="Fem siffror, t.ex. 553 20"
                                    maxLength={6}
                                    placeholder="Postnummer"
                                    value={form.postalCode}
                                    onChange={e => set({ postalCode: e.target.value })}
                                />
                                <Input
                                    id="city"
                                    name="city"
                                    required
                                    aria-label="Ort"
                                    autoComplete="shipping address-level2"
                                    minLength={2}
                                    maxLength={80}
                                    placeholder="Ort"
                                    value={form.city}
                                    onChange={e => set({ city: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Terms Acceptance (Mandatory) */}
                <div className="flex items-start gap-3 px-2 py-1">
                    <input
                        id="terms-accepted"
                        type="checkbox"
                        required
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                        className="mt-0.5 h-[18px] w-[18px] flex-shrink-0 rounded border-stone-300 accent-green-600 cursor-pointer"
                    />
                    <label htmlFor="terms-accepted" className="flex-1 text-[11px] text-stone-500 leading-snug cursor-pointer">
                        Jag godkänner <a href="#/info/terms" target="_blank" rel="noopener noreferrer" className="underline text-stone-700 hover:text-coffee-600 font-bold">köpvillkoren</a> och har tagit del av <a href="#/info/privacy" target="_blank" rel="noopener noreferrer" className="underline text-stone-700 hover:text-coffee-600 font-bold">integritetspolicyn</a>.
                    </label>
                </div>

                <div className="pt-2">
                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={!termsAccepted || isSubmitting || disabled}
                        icon={!isSubmitting && (paysInCheckout ? <Lock size={16} /> : <ArrowRight size={16} />)}
                        className={!termsAccepted ? 'opacity-50 cursor-not-allowed' : ''}
                    >
                        {isSubmitting ? 'Behandlar...' : paysInCheckout ? 'Fortsätt till betalning' : `Beställ och betala ${sellerFirstName}`}
                    </Button>
                </div>

                <div className="flex items-center justify-center gap-2 text-[9px] text-stone-300 pt-2 pb-4">
                    <ShieldCheck size={12} />
                    <span>Säker hantering av personuppgifter</span>
                </div>
            </FormContainer>
        </form>
    );
};
